/**
 * ISP-9999 (Option B4, fe-subB4-plan-move-flow): pure logic for the
 * "Move my plan" panel (Settings -> Subscription/Billing).
 *
 * A store subscriber (apple_iap/google_play) cannot change their web plan
 * cards today -- every card renders "Manage in App Store" instead (see
 * SubscriptionItem.jsx's `appStoreManaged` branch, SUB-A4). Behind
 * settings.ENTITLEMENT_LEDGER_ENABLED (Option B1, read from the API --
 * `entitlement_ledger_enabled` on `/subscription/packages/`'s package rows,
 * NOT a REACT_APP_* build flag, so it can be flipped without a redeploy),
 * this resolves which of the panel's states to show for a given
 * (paymentSource, movePlanPeriodEnd) pair. Extracted behavior-first so the
 * four states can be tested without mounting Settings/Subscription/index.jsx's
 * full (redux + react-query + MUI) tree.
 */

export const MOVE_PLAN_STATES = {
  // Flag OFF, or paymentSource isn't a recognized value at all (e.g. no
  // subscription yet) -- panel renders nothing.
  HIDDEN: "hidden",
  // Flag ON, store source, but no active Entitlement row was found (ledger
  // not yet populated/backfilled for this user) -- nothing to anchor a
  // checkout to.
  NO_ENTITLEMENT: "no_entitlement",
  // Flag ON, store source, live Entitlement with a period end -- the normal
  // "start your web plan, billed from <date>" state.
  READY: "ready",
  // Flag ON, but the user is already on a web (stripe) plan -- nothing to
  // move.
  ALREADY_WEB: "already_web",
};

const STORE_SOURCES = ["apple_iap", "google_play"];
const WEB_SOURCES = ["stripe", "web"];

/**
 * @param {{entitlementLedgerEnabled: boolean, paymentSource: string|null|undefined, movePlanPeriodEnd: string|null|undefined}} args
 * @returns {string} one of MOVE_PLAN_STATES
 */
export const resolveMovePlanState = ({
  entitlementLedgerEnabled,
  paymentSource,
  movePlanPeriodEnd,
}) => {
  if (!entitlementLedgerEnabled) return MOVE_PLAN_STATES.HIDDEN;
  if (STORE_SOURCES.includes(paymentSource)) {
    return movePlanPeriodEnd
      ? MOVE_PLAN_STATES.READY
      : MOVE_PLAN_STATES.NO_ENTITLEMENT;
  }
  if (WEB_SOURCES.includes(paymentSource)) {
    return MOVE_PLAN_STATES.ALREADY_WEB;
  }
  return MOVE_PLAN_STATES.HIDDEN;
};

/** Human date, same formatting convention as index.jsx's currentPlanRenewalDate. */
export const formatMovePlanDate = (iso) => {
  if (!iso || Number.isNaN(Date.parse(iso))) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};
